import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import CompanyEconomicsSection from './company-economics-section';
import SupplierControls from '../app-add-supplier/supplier-controls';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import { ReactComponent as InfoIcon } from '../../assets/svg/InfoIconLight.svg';
import { ServiceSalesTool } from '../../services/service-sales-tool';
import './page-sales-tool.scss';

// SYNA/CreditSafe responses are parsed server-side (SupplierEconomicsService.parseSynaResponse
// and parseCreditsafe) into the shared shape below. Both the sales-tool page and the supplier
// Economics tab consume that already-parsed shape, so there's no client-side parser to keep in
// sync — see src/services/supplierEconomicsService.js.

export interface KPIYear {
  period: string;
  // Numeric KPIs are null when the provider has no value (renders as "-", not 0).
  omsattning: number | null;
  resultat: number | null;
  balansomslutning: number | null;
  egetKapital: number | null;
  soliditet: string;
  anstallda: number | null;
  kassalikviditet: string;
  // Additional figures surfaced in the Economics tab's "See all". Optional/nullable because
  // availability differs by provider (SYNA BNyck vs CreditSafe) — absent ⇒ the row is hidden.
  nettoomsattning?: number | null;
  omsattningstillvaxt?: string | null;   // revenue growth % (SYNA OmsChg; derived for CreditSafe)
  rorelseresultat?: number | null;       // operating profit (CreditSafe only)
  aretsresultat?: number | null;         // net profit / årets resultat (CreditSafe only)
  summaSkulder?: number | null;          // total liabilities (CreditSafe only)
  omsattningPerAnstalld?: number | null;
  rorelsemarginal?: string;
  vinstmarginal?: string;
  avkastningEgetKapital?: string;
}

export interface GroupedUser {
  name: string;
  email: string;
  title: string;
  phones: string[];
  orgs: string[];
  totalRequests: number;
}

export const groupSupplierUsers = (supplierUsers: any[]): GroupedUser[] => {
  if (!supplierUsers || supplierUsers.length === 0) return [];
  const grouped = new Map<string, GroupedUser>();
  for (const u of supplierUsers) {
    const key = (u.email || '').toLowerCase();
    if (!grouped.has(key)) {
      grouped.set(key, { name: u.name, email: u.email, title: u.title || '', phones: [], orgs: [], totalRequests: 0 });
    }
    const g = grouped.get(key)!;
    g.totalRequests += u.requestCount || 0;
    if (u.orgName && !g.orgs.includes(u.orgName)) g.orgs.push(u.orgName);
    if (u.phone && !g.phones.includes(u.phone)) g.phones.push(u.phone);
    if (!g.title && u.title) g.title = u.title;
  }
  return Array.from(grouped.values()).filter(g => g.email && g.name);
};

type RouteTypes = RouteComponentProps<{}, Record<string, unknown>>;

export type SupplierCompanyCardData = {
  companyInfo: any;
  supplierStats: any;
  ratingInfo: any;
  kpiData: KPIYear[];
  groupedUsers: GroupedUser[];
  reachData: any;
  loading: boolean;
};

export const useSupplierCompanyCardData = (organizationNumber?: string): SupplierCompanyCardData => {
  const [companyData, setCompanyData] = useState<any>(null);
  const [supplierStats, setSupplierStats] = useState<any>(null);
  const [supplierUsers, setSupplierUsers] = useState<any[]>([]);
  const [reachData, setReachData] = useState<any>(null);
  const [loading, setLoading] = useState(Boolean(organizationNumber));

  useEffect(() => {
    if (!organizationNumber) return;
    let cancelled = false;
    const orgNr = organizationNumber.replace(/[-\s]/g, '');
    setLoading(true);
    setCompanyData(null);
    setSupplierStats(null);
    setSupplierUsers([]);
    setReachData(null);

    (async () => {
      try {
        const [company, stats] = await Promise.all([
          ServiceSalesTool.companyLookup(organizationNumber).catch(() => null),
          ServiceSalesTool.getSupplierStats(orgNr).catch(() => null),
        ]);
        if (cancelled) return;
        setCompanyData(company);
        setSupplierStats(stats);

        const [users, reach] = await Promise.all([
          ServiceSalesTool.getSupplierUsers(orgNr).catch(() => []),
          ServiceSalesTool.getSupplierReach(orgNr, stats?.categories || []).catch(() => null),
        ]);
        if (cancelled) return;
        setSupplierUsers(users);
        setReachData(reach);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [organizationNumber]);

  // companyData is the server-parsed lookup response ({ companyInfo, ratingInfo, kpiData }).
  const companyInfo = useMemo(() => companyData?.companyInfo ?? null, [companyData]);
  const ratingInfo = useMemo(() => companyData?.ratingInfo ?? null, [companyData]);
  const kpiData = useMemo<KPIYear[]>(() => companyData?.kpiData ?? [], [companyData]);
  const groupedUsers = useMemo(() => groupSupplierUsers(supplierUsers), [supplierUsers]);

  return { companyInfo, supplierStats, ratingInfo, kpiData, groupedUsers, reachData, loading };
};

type Props = {
  companyInfo: any;
  supplierStats: any;
  ratingInfo: any;
  kpiData: KPIYear[];
  groupedUsers: GroupedUser[];
  reachData: any;
  history: RouteTypes['history'];
  hasSyna?: boolean;
  hasCreditsafe?: boolean;
  hasTic?: boolean;
  // Fallback identity lets the card render its header/controls from the caller's known
  // values when the Syna lookup returns nothing.
  fallbackCompanyName?: string;
  fallbackOrganizationNumber?: string;
};

const SupplierCompanyCard: FC<Props> = ({
  companyInfo,
  supplierStats,
  ratingInfo,
  kpiData,
  groupedUsers,
  reachData,
  history,
  hasSyna = true,
  hasCreditsafe = false,
  hasTic = false,
  fallbackCompanyName,
  fallbackOrganizationNumber,
}) => {
  const [t] = useTranslation('salesTool');

  const bidRequestMap = useMemo(() => {
    const map = new Map<string, number>();
    (supplierStats?.bidRequestOrgs || []).forEach((org: any) => {
      map.set(org.orgName, org.count);
    });
    return map;
  }, [supplierStats]);

  const displayName = companyInfo?.name ?? fallbackCompanyName;
  // Prefer the fallback (stable, available immediately) for the controls so they mount on
  // first render and don't refetch when the Syna lookup resolves.
  const controlsOrgNumber = fallbackOrganizationNumber ?? supplierStats?.organizationNumber;
  const controlsCompanyName = fallbackCompanyName ?? (supplierStats?.name || companyInfo?.name);

  // Render without companyInfo only when a fallback identity is supplied (the detail-panel
  // use case). The sales-tool page passes none, so it still renders nothing until lookup resolves.
  if (!companyInfo && !fallbackOrganizationNumber) return null;

  return (
    <div className="sales-tool__company-card">
      <div className="sales-tool__company-card-header">
        <span className="sales-tool__company-card-name">{displayName}</span>
        <div className="sales-tool__company-card-badges">
          {supplierStats?.accountTypes?.includes('SUPERADMIN') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--superadmin">{t('badge.superadmin', 'Superadmin')}</span>
          )}
          {supplierStats?.accountTypes?.includes('PURCHASING') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--purchasing">{t('badge.purchasing', 'Inköp')}</span>
          )}
          {supplierStats?.accountTypes?.includes('SALES') && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--sales">{t('badge.sales', 'Leverantör')}</span>
          )}
          {supplierStats?.subscriptions?.map((sub: any) => (
            <span key={sub.product} className="sales-tool__company-card-badge sales-tool__company-card-badge--subscription">
              {t(`subscription.${sub.product}`, sub.product) as string}
            </span>
          ))}
          {!supplierStats?.accountTypes?.filter((type: string) => type !== 'SUPERADMIN').length &&
            !supplierStats?.accountTypes?.includes('SUPERADMIN') && supplierStats && (
            <span className="sales-tool__company-card-badge sales-tool__company-card-badge--none">{t('badge.noAccount', 'Ej konto')}</span>
          )}
        </div>
      </div>
      <CompanyEconomicsSection
        companyInfo={companyInfo}
        ratingInfo={ratingInfo}
        kpiData={kpiData}
        fallbackOrgNumber={fallbackOrganizationNumber}
      />

      {/* Supplier Stats inside company card */}
      {supplierStats && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('section.activity', 'Aktivitet på Accurator')}</h4>
          <div className="sales-tool__company-card-stats">
            {[
              {
                value: supplierStats.customerRegisters?.count || 0,
                label: t('activity.customerRegisters.label', 'Kundregister'),
                tooltip: t('activity.customerRegisters.tooltip', 'Antal aktiva kundorganisationer (exkl. demo/superadmin) som har en leverantörspost med detta organisationsnummer i sitt register.'),
              },
              {
                value: reachData?.currentCustomers ?? '-',
                label: t('activity.currentCustomers.label', 'Kunder idag'),
                tooltip: t('activity.currentCustomers.tooltip', 'Antal unika organisationer som har skickat minst en förfrågan till denna leverantör. Räknas via projektens orgId i aktiva, ej borttagna projekt.'),
              },
              {
                value: supplierStats.bidRequests?.received || 0,
                label: t('activity.receivedRequests.label', 'Mottagna förfrågningar'),
                tooltip: t('activity.receivedRequests.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören finns med i suppliers-arrayen i versions-kollektionen. Räknas över alla organisationer.'),
              },
              {
                value: supplierStats.bidRequests?.submitted || 0,
                label: t('activity.submittedBids.label', 'Inlämnade anbud'),
                tooltip: t('activity.submittedBids.tooltip', 'Antal unika inköp (projectId+areaId) där leverantören har en supplierBids-post i någon version. Inkluderar bud på borttagna projekt.'),
              },
              {
                value: supplierStats.wonDeals || 0,
                label: t('activity.wonDeals.label', 'Vunna affärer'),
                tooltip: t('activity.wonDeals.tooltip', 'Antal inköp där leverantören är tilldelad (reward: true) OCH det finns ett signerat Scrive-kontrakt eller manuellt kontrakt. Enbart aktiva, ej borttagna projekt.'),
              },
              {
                value: groupedUsers.length || 0,
                label: t('activity.contacts.label', 'Kontaktpersoner'),
                tooltip: t('activity.contacts.tooltip', 'Antal unika e-postadresser bland aktiva användare (isActive: true) kopplade till leverantörens supplier-poster. Exkl. demo/superadmin-organisationer.'),
              },
            ].map(stat => (
              <TooltipContainer
                key={stat.label}
                renderReferenceComponent={(className, ref) => (
                  <div className={`sales-tool__company-card-stat ${className}`} ref={ref}>
                    <span className="sales-tool__company-card-stat-value">{stat.value}</span>
                    <span className="sales-tool__company-card-stat-label">{stat.label}</span>
                  </div>
                )}
              >
                <div style={{ maxWidth: 260, fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                  <strong style={{ display: 'block', marginBottom: 4 }}>{stat.label}</strong>
                  {stat.tooltip}
                </div>
              </TooltipContainer>
            ))}
          </div>
        </>
      )}

      {/* Supplier Controls inside company card */}
      {controlsOrgNumber && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">{t('section.supplierControls', 'Leverantörskontroller')}</h4>
          <div className="sales-tool__company-card-controls">
            <SupplierControls
              organizationNumber={controlsOrgNumber}
              companyName={controlsCompanyName}
              hasSyna={hasSyna}
              hasCreditsafe={hasCreditsafe}
              hasTic={hasTic}
              history={history}
            />
          </div>
        </>
      )}

      {/* Customer Organizations + Users + Bid Request Orgs */}
      {supplierStats && (
        <>
          <div className="sales-tool__company-card-divider" />
          <h4 className="sales-tool__company-card-subtitle">
            {t('section.orgsWithSupplier', 'Organisationer med leverantören i sitt register ({{count}})', { count: supplierStats.customerRegisters?.count || 0 })}
          </h4>

          <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>{t('table.organization', 'Organisation')}</th>
                  <th>{t('table.status', 'Status')}</th>
                  <th>{t('table.requestCount', 'Antal förfrågningar')}</th>
                </tr>
              </thead>
              <tbody>
                {supplierStats.customerRegisters?.organizations?.length > 0 ? (
                  supplierStats.customerRegisters.organizations.map((org: any, i: number) => {
                    const status = org.prequalification || 'APPROVED';
                    const label = status === 'APPROVED' ? t('status.approved', 'Godkänd') : status === 'REJECTED' ? t('status.rejected', 'Nekad') : t('status.pending', 'Ansökan');
                    const badgeClass = status === 'APPROVED' ? 'sales-tool__status-badge--approved'
                      : status === 'REJECTED' ? 'sales-tool__status-badge--rejected'
                      : 'sales-tool__status-badge--pending';
                    const bidCount = bidRequestMap.get(org.orgName);
                    return (
                      <tr key={i}>
                        <td>{org.orgName}</td>
                        <td><span className={`sales-tool__status-badge ${badgeClass}`}>{label}</span></td>
                        <td>{bidCount != null ? `${bidCount} ${t('count.suffix', 'st')}` : '–'}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                    {t('empty.noOrgsWithSupplier', 'Inga organisationer har denna leverantör i sitt register.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Users as subsection — grouped by email */}
          <h4 className="sales-tool__company-card-subtitle">
            {t('section.contacts', 'Kontaktpersoner ({{count}})', { count: groupedUsers.length })}
            <TooltipContainer
              renderReferenceComponent={(className, ref) => (
                <span ref={ref} className={className} style={{ marginLeft: 6, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}><InfoIcon /></span>
              )}
            >
              <div style={{ maxWidth: 260, fontSize: 13, padding: '4px 0' }}>
                {t('contacts.tooltip', 'Kontaktpersoner som finns hos Accurator-användares register')}
              </div>
            </TooltipContainer>
          </h4>
          <div className="sales-tool__supplier-users" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>{t('table.name', 'Namn')}</th>
                  <th>{t('table.email', 'E-post')}</th>
                  <th>{t('table.phone', 'Telefon')}</th>
                  <th>{t('table.title', 'Titel')}</th>
                  <th>{t('table.requests', 'Förfrågningar')}</th>
                </tr>
              </thead>
              <tbody>
                {groupedUsers.length > 0 ? (
                  groupedUsers.map((user, i) => (
                    <TooltipContainer
                      key={i}
                      renderReferenceComponent={(className, ref) => (
                        <tr className={className} ref={ref}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            {user.phones[0] || '-'}
                            {user.phones.length > 1 && (
                              <TooltipContainer
                                renderReferenceComponent={(cn, r) => (
                                  <span className={cn} ref={r} style={{ marginLeft: 4, cursor: 'help', verticalAlign: 'middle', display: 'inline-flex' }}>
                                    <InfoIcon />
                                  </span>
                                )}
                              >
                                <div style={{ fontSize: 13, padding: '4px 0' }}>
                                  <strong>{t('contacts.otherPhones', 'Övriga telefonnummer:')}</strong>
                                  {user.phones.slice(1).map((p, j) => <div key={j}>{p}</div>)}
                                </div>
                              </TooltipContainer>
                            )}
                          </td>
                          <td>{user.title || '-'}</td>
                          <td>{user.totalRequests} {t('count.suffix', 'st')}</td>
                        </tr>
                      )}
                    >
                      {user.orgs.length > 0 && (
                        <div style={{ fontSize: 13, color: 'var(--gray-700)', padding: '4px 0' }}>
                          <strong>{user.orgs.length === 1 ? t('contacts.organization', 'Organisation:') : t('contacts.organizations', 'Organisationer:')}</strong>
                          {user.orgs.map((org, j) => <div key={j}>{org}</div>)}
                        </div>
                      )}
                    </TooltipContainer>
                  ))
                ) : (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 0' }}>
                    {t('empty.noUsers', 'Inga registrerade användare hos leverantören.')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Show orgs that sent bids but are NOT in the register */}
          {supplierStats.bidRequestOrgs?.some((org: any) =>
            !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName)
          ) && (
            <div className="sales-tool__supplier-users" style={{ marginTop: -8 }}>
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={{ fontWeight: 500, fontSize: 13, color: 'var(--gray-500)', paddingTop: 4 }}>
                      {t('table.otherOrgsWithRequests', 'Övriga organisationer som skickat förfrågningar')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {supplierStats.bidRequestOrgs
                    .filter((org: any) => !supplierStats.customerRegisters?.organizations?.some((r: any) => r.orgName === org.orgName))
                    .map((org: any, i: number) => (
                      <tr key={i}>
                        <td>{org.orgName}</td>
                        <td>–</td>
                        <td>{org.count} {t('count.suffix', 'st')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SupplierCompanyCard;
